import React from 'react';
import PropTypes from 'prop-types';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {resolveAnimationFromName, animationDelayDefault} from '../config/animationMap';
import styles from './animations-hashed.scss';
import {log} from '@elixir/logger';

const AnimationGroup = props => {
    const {animation, children, ...other} = props;

    // Look up animation props by name and use
    let animationProps = resolveAnimationFromName(animation, false);
    if (animation && !animationProps) {
        // warn and set defaults
        log.warning(`Unable to find animation alias for '${animation}'. Using default properties.`);
        animationProps = {
            classNames: animation,
            timeout: animationDelayDefault
        };
    }

    const combinedProps = Object.assign({}, animationProps, other);

    // convert classNames into custom styles (to support css modules)
    const classNames = combinedProps.classNames;

    const enterClasses = {
        enter: styles[`${classNames}_enter`],
        enterActive: styles[`${classNames}_enter_active`]
    };

    const exitClasses = {
        exit: styles[`${classNames}_exit`],
        exitActive: styles[`${classNames}_exit_active`]
    };

    let appearClasses;
    if (combinedProps.appear) {
        appearClasses = {
            appear: styles[`${classNames}_enter`],
            appearActive: styles[`${classNames}_enter_active`]
        };
    }

    combinedProps.classNames = Object.assign({}, enterClasses, exitClasses, appearClasses);

    const {transitionWrapper, ...groupProps} = combinedProps;
    const kidCount = React.Children.count(children);
    let transitionGroup;
    if (kidCount > 1) {
        const childTransitions = React.Children.map(children, (child, index) => {
            return (
                <CSSTransition key={child.props.key || index} {...groupProps}>
                    {child}
                </CSSTransition>
            );
        });
        transitionGroup = (
            <TransitionGroup>{childTransitions}</TransitionGroup>
        );
    } else {
        transitionGroup = (
            <CSSTransition {...groupProps}>{children}</CSSTransition>
        );
    }

    if (transitionWrapper) {
        const wrapperClass = styles[transitionWrapper];
        return (
            <div className={wrapperClass}>
                {transitionGroup}
            </div>
        );
    }

    return transitionGroup;
};

/**
 * @typedef {Object} AnimationGroup.propTypes
 * @property {string} [animation] - Optional animation name/alias
 * @property {string} [className] - Optional css class(es) to add to the grid element
 * @property {PropTypes.node} [children] - children; preferably 'Row'
 */
AnimationGroup.propTypes = {
    animation: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node
};

/**
 * Defines an Animation Group.
 * This wraps TransitionGroup/CSSTransition and provides support for animation aliases to define transition properties.
 * The animations are triggered when elements are added to or removed from the children of the animation group.
 * For the 'swap' type animations where you are replacing one element with another, each element requires a unique
 * 'key' to trigger the animation.
 *
 * Properties: {@link AnimationGroup.propTypes}
 *
 * @param {Object} props - the properties to use for this AnimationGroup
 *
 * @see https://github.com/reactjs/react-transition-group
 *
 * @example <AnimationGroup animation="reveal">{ children to animate }</AnimationGroup>
 */
export default AnimationGroup;
